import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { GetLoginCredentials, OAuthConnected } from '../Service/AxiosService';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}   

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize synchronously from sessionStorage so the FIRST render already reflects the logged-in
  // state. Otherwise the router briefly sees isAuthenticated=false on refresh and does
  // <Navigate to="/login" replace>, which rewrites the URL to /login even though the session is valid.
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem('isAuthenticated') === 'true'
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('username');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const redirectToNetworkError = useCallback(() => {
    window.location.href = '/networkError';
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  // Auth state is rehydrated synchronously in the useState initializer above; here we only do the
  // network reachability check.
  useEffect(() => {
    const checkNetwork = async () => {
      const network = await networkCheck();
      if (network.error == 'Network Error') {
        redirectToNetworkError();
      }
    };
    checkNetwork();
  }, []);

 var networkCheck = async () => {
  return await OAuthConnected();
 }
  // Inactivity auto-logout
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Start the timer
    resetInactivityTimer();

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      // Cleanup
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  const login = async (username: string, password: string): Promise<boolean> => {
    const result = await GetLoginCredentials(username, password);

    if (result.success && result.data && result.data.token) {
      sessionStorage.setItem('token', result.data.token);
      setIsAuthenticated(true);
      sessionStorage.setItem('isAuthenticated', 'true');
      sessionStorage.setItem('username', username);
      return true;
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
